import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { useAnalytics, type TimeRange, type WorkflowStat, type ListMeta, type CustomRange } from '@/hooks/useAnalytics';
import { VizRoot, Timeseries, BarList, Donut, CATEGORICAL, statusColor } from '@/components/analytics/charts';
import {
  BarChart3,
  Loader2,
  TrendingUp,
  Users,
  CheckCircle2,
  Timer,
  Hourglass,
  AlertTriangle,
  X,
  Search,
  RefreshCw,
  CalendarRange,
} from 'lucide-react';

const RANGES: { value: TimeRange; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: 'all', label: 'All' },
];

function fmtDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}

function fmtPct(v: number): string {
  return `${Math.round(v * 100)}%`;
}

// "Showing top 12 of 40" — only says "top N" when the list was actually capped.
function workflowsCaption(meta: ListMeta): string {
  if (meta.total === 0) return 'No workflows in range';
  if (meta.total <= meta.shown) {
    return `Showing all ${meta.total} workflow${meta.total === 1 ? '' : 's'}`;
  }
  return `Showing top ${meta.shown} of ${meta.total} workflows`;
}

function topUsersCaption(meta: ListMeta): string {
  if (meta.total === 0) return 'No users in range';
  if (meta.total <= meta.shown) {
    return `Showing all ${meta.total} user${meta.total === 1 ? '' : 's'}`;
  }
  return `Showing top ${meta.shown} of ${meta.total} users`;
}

function StatTile({
  icon: Icon,
  label,
  value,
  hint,
  accent,
}: {
  icon: typeof TrendingUp;
  label: string;
  value: string;
  hint?: string;
  accent?: string;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-muted-foreground mb-2">
          <Icon className="h-4 w-4" style={accent ? { color: accent } : undefined} />
          <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
        </div>
        <div className="text-3xl font-semibold tabular-nums leading-none">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1.5">{hint}</div>}
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  const {
    stats,
    loading,
    error,
    range,
    setRange,
    customRange,
    setCustomRange,
    workflowId,
    setWorkflowId,
    workflowSearch,
    setWorkflowSearch,
    refetch,
  } = useAnalytics('24h');

  const selectedWorkflow = workflowId ? stats?.workflows.find((w) => w.workflowId === workflowId) : null;
  const customActive = customRange != null;

  return (
    <div className="max-w-[1600px] mx-auto p-6 space-y-6">
      {/* Header + filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Workflow execution metrics and usage.</p>
        </div>
        <div className="flex items-center gap-2">
          {workflowId && (
            <button
              onClick={() => setWorkflowId(null)}
              className="flex items-center gap-1.5 text-sm rounded-md border px-2.5 py-1.5 bg-secondary hover:bg-accent transition-colors"
            >
              <span className="truncate max-w-[160px]">{selectedWorkflow?.workflowName ?? workflowId}</span>
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <div className="inline-flex rounded-md border overflow-hidden">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setRange(r.value)}
                className={cn(
                  'px-3 py-1.5 text-sm transition-colors border-r last:border-r-0',
                  range === r.value && !customActive
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent text-muted-foreground',
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
          <DateRangePicker value={customRange} onChange={setCustomRange} active={customActive} />
          <button
            onClick={refetch}
            disabled={loading}
            title="Refresh"
            className={cn(
              'flex items-center justify-center h-9 w-9 rounded-md border transition-colors disabled:opacity-50',
              'text-muted-foreground hover:text-foreground hover:bg-accent',
            )}
          >
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </div>
      )}

      {loading && !stats ? (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          <Loader2 className="h-10 w-10 animate-spin" />
        </div>
      ) : stats ? (
        <div className="relative">
          {/* Refetch overlay: dim the stale view and float a prominent spinner so
              a range/filter change never reads as a frozen dashboard. Fixed to
              the viewport so it stays centered even when the user has scrolled. */}
          {loading && (
            <div className="fixed inset-0 z-20 flex items-center justify-center pointer-events-none">
              <div className="flex flex-col items-center gap-2 rounded-lg border bg-background/95 px-6 py-4 shadow-lg">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="text-sm text-muted-foreground">Loading…</span>
              </div>
            </div>
          )}
          <VizRoot
            className={cn('space-y-6 transition-opacity', loading && 'opacity-40 pointer-events-none')}
            aria-busy={loading}
          >
            {/* Stat tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
              <StatTile icon={TrendingUp} label="Total Runs" value={stats.summary.totalRuns.toLocaleString()} />
              <StatTile
                icon={CheckCircle2}
                label="Success Rate"
                value={fmtPct(stats.summary.successRate)}
                hint={`${stats.summary.completed.toLocaleString()} completed`}
                accent="var(--status-good)"
              />
              <StatTile
                icon={AlertTriangle}
                label="Errors"
                value={stats.summary.errors.toLocaleString()}
                accent="var(--status-critical)"
              />
              <StatTile icon={Users} label="Active Users" value={stats.summary.activeUsers.toLocaleString()} />
              <StatTile
                icon={Timer}
                label="Median Run Time"
                value={fmtDuration(stats.summary.medianRunMs)}
                hint="start → completed"
              />
              <StatTile
                icon={Hourglass}
                label="Median Queue Wait"
                value={fmtDuration(stats.summary.medianQueueMs)}
                hint="submitted → start"
              />
            </div>

            {/* Timeseries + status donut */}
            <div className="grid lg:grid-cols-3 gap-4">
              <Card className="lg:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold">
                    {stats.granularity === 'hour' ? 'Runs per hour' : 'Runs per day'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Timeseries data={stats.timeseries} granularity={stats.granularity} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold">Status breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <Donut
                    data={stats.statusBreakdown.map((s) => ({
                      label: s.status,
                      value: s.count,
                      color: statusColor(s.status),
                    }))}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Workflows (wide) + top users, two columns on large screens */}
            <div className="grid lg:grid-cols-3 gap-4 items-start">
              <Card className="lg:col-span-2">
                <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3 space-y-0">
                  <div>
                    <CardTitle className="text-base font-semibold">Runs per workflow</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">{workflowsCaption(stats.workflowsMeta)}</p>
                  </div>
                  <div className="relative w-48 sm:w-56">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input
                      type="text"
                      value={workflowSearch}
                      onChange={(e) => setWorkflowSearch(e.target.value)}
                      placeholder="Search workflows…"
                      className="w-full rounded-md border bg-background pl-8 pr-8 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
                    />
                    {workflowSearch && (
                      <button
                        onClick={() => setWorkflowSearch('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        aria-label="Clear search"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {stats.workflows.length === 0 ? (
                    <div className="flex items-center justify-center h-24 text-sm text-muted-foreground">
                      {stats.workflowSearch ? `No workflows match “${stats.workflowSearch}”.` : 'No workflows in range.'}
                    </div>
                  ) : (
                    <WorkflowBars workflows={stats.workflows} activeId={workflowId} onSelect={setWorkflowId} />
                  )}
                </CardContent>
              </Card>

              {/* Top users */}
              {stats.topUsers.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-semibold">Top users</CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">{topUsersCaption(stats.topUsersMeta)}</p>
                  </CardHeader>
                  <CardContent>
                    <BarList
                      data={stats.topUsers.map((u) => ({
                        label: u.name ?? u.uid ?? 'Unknown',
                        value: u.runs,
                        color: 'var(--series-1)',
                      }))}
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            <p className="text-xs text-muted-foreground text-right">Updated {new Date(stats.generatedAt).toLocaleString()}</p>
          </VizRoot>
        </div>
      ) : null}
    </div>
  );
}

// Custom date-range control: a popover with from/to date inputs. Applying sets
// the custom window (which overrides the named range); Clear reverts to presets.
function DateRangePicker({
  value,
  onChange,
  active,
}: {
  value: CustomRange | null;
  onChange: (r: CustomRange | null) => void;
  active: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(value?.from ?? '');
  const [to, setTo] = useState(value?.to ?? '');

  const apply = () => {
    if (!from && !to) {
      onChange(null);
    } else {
      onChange({ from, to });
    }
    setOpen(false);
  };

  const clear = () => {
    setFrom('');
    setTo('');
    onChange(null);
    setOpen(false);
  };

  const label = active && value ? `${value.from || '…'} → ${value.to || '…'}` : 'Custom';

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex items-center gap-1.5 h-9 rounded-md border px-3 text-sm transition-colors',
          active ? 'bg-primary text-primary-foreground' : 'hover:bg-accent text-muted-foreground',
        )}
      >
        <CalendarRange className="h-4 w-4" />
        <span className="max-w-[180px] truncate">{label}</span>
      </button>
      {open && (
        <>
          {/* Click-away backdrop */}
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 z-30 w-64 rounded-md border bg-popover p-3 shadow-lg space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">From</label>
              <input
                type="date"
                value={from}
                max={to || undefined}
                onChange={(e) => setFrom(e.target.value)}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">To</label>
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => setTo(e.target.value)}
                className="w-full rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="flex items-center justify-between pt-1">
              <button onClick={clear} className="text-sm text-muted-foreground hover:text-foreground">
                Clear
              </button>
              <button
                onClick={apply}
                disabled={!from && !to}
                className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Merged bar + table: each row is a clickable magnitude bar (runs) carrying the
// per-workflow stats inline — runs, completed/errors, success rate and median
// run time / queue wait — so there's no separate table to scan against.
function WorkflowBars({
  workflows,
  activeId,
  onSelect,
}: {
  workflows: WorkflowStat[];
  activeId: string | null;
  onSelect: (id: string | null) => void;
}) {
  if (workflows.length === 0) return null;
  const max = Math.max(1, ...workflows.map((w) => w.runs));

  return (
    <div className="space-y-1">
      {workflows.map((w, i) => {
        const isActive = activeId === w.workflowId;
        const dimmed = activeId != null && !isActive;
        return (
          <button
            key={w.workflowId ?? `unknown-${i}`}
            onClick={() => w.workflowId && onSelect(isActive ? null : w.workflowId)}
            className={cn(
              'w-full text-left rounded-md px-2 py-2 transition-colors',
              isActive ? 'bg-accent' : 'hover:bg-accent/50',
            )}
          >
            {/* Row 1: name + run count */}
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <span className="text-sm truncate" title={w.workflowName ?? w.workflowId ?? 'Unknown'}>
                {w.workflowName ?? w.workflowId ?? 'Unknown'}
              </span>
              <span className="text-sm font-medium tabular-nums shrink-0">{w.runs.toLocaleString()} runs</span>
            </div>
            {/* Row 2: magnitude bar */}
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-300"
                style={{
                  width: `${Math.max(2, (w.runs / max) * 100)}%`,
                  backgroundColor: CATEGORICAL[i % CATEGORICAL.length],
                  opacity: dimmed ? 0.5 : 1,
                }}
              />
            </div>
            {/* Row 3: inline stats (the old table columns) */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1.5 text-xs text-muted-foreground tabular-nums">
              <span style={{ color: 'var(--status-good)' }}>{w.completed.toLocaleString()} completed</span>
              <span style={{ color: 'var(--status-critical)' }}>{w.errors.toLocaleString()} errors</span>
              <span>{fmtPct(w.successRate)} success</span>
              <span>{fmtDuration(w.medianRunMs)} median run</span>
              <span>{fmtDuration(w.medianQueueMs)} median queue</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
